import { useEffect, useState } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { useAuthUrl } from "../utils/authRedirect";
import { useSelector } from "react-redux";
import { Container, Row, Col, Button, Alert, Badge, Spinner, Input, Label, FormGroup } from "reactstrap";
import requests from "../requests";
import { usePostHog } from "@posthog/react";
import { useSeo } from "../hooks/useSeo";

interface EmailPrefs {
  digest_subscribed: boolean;
  alerts_subscribed: boolean;
}

interface DisplayNameResponse {
  display_name: string | null;
}

interface SubscriptionStatus {
  plan: "free" | "pro";
  status: string | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  trial_end: number | null;
}

function formatDate(ts: number | null): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function statusBadge(status: string | null) {
  if (!status) return null;
  const colors: Record<string, string> = {
    active: "success",
    trialing: "info",
    past_due: "warning",
    canceled: "secondary",
    unpaid: "danger",
  };
  return (
    <Badge color={colors[status] ?? "secondary"} className="ms-2" style={{ textTransform: "capitalize" }}>
      {status === "trialing" ? "Free Trial" : status.replace("_", " ")}
    </Badge>
  );
}

export default function Account() {
  useSeo({ title: "Account", path: "/account", noindex: true });
  const authUrlFor = useAuthUrl();
  const loggedIn = useSelector((state: RootReducerState) => state.authentication.loggedIn);
  const authChecked = useSelector((state: RootReducerState) => state.authentication.authChecked);
  const user = useSelector((state: RootReducerState) => state.authentication.user);
  const isPro = user?.groups?.includes("pro") ?? false;
  const isAdmin = (user as { groups?: string[] } | null)?.groups?.includes("admin") ?? false;

  const posthog = usePostHog();
  const [sub, setSub] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailPrefs, setEmailPrefs] = useState<EmailPrefs | null>(null);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsError, setPrefsError] = useState("");
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [displayNameInput, setDisplayNameInput] = useState("");
  const [displayNameLoaded, setDisplayNameLoaded] = useState(false);
  const [displayNameSaving, setDisplayNameSaving] = useState(false);
  const [displayNameError, setDisplayNameError] = useState("");
  const [searchParams] = useSearchParams();
  const checkoutSuccess = searchParams.get("checkout") === "success";

  useEffect(() => {
    requests
      .get("/api/billing/subscription")
      .then((res) => setSub(res.data))
      .catch(() => setSub({ plan: "free", status: null, current_period_end: null, cancel_at_period_end: false, trial_end: null }))
      .finally(() => setLoading(false));
    requests
      .get("/api/email/prefs")
      .then((res) => setEmailPrefs(res.data))
      .catch(() => setEmailPrefs(null));
    requests
      .get<DisplayNameResponse>("/api/account/display-name")
      .then((res) => {
        setDisplayName(res.data.display_name);
        setDisplayNameInput(res.data.display_name ?? "");
      })
      .catch(() => undefined)
      .finally(() => setDisplayNameLoaded(true));
  }, []);

  useEffect(() => {
    if (checkoutSuccess) posthog.capture("checkout_success_viewed");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutSuccess]);

  const updateEmailPref = async (key: keyof EmailPrefs, value: boolean) => {
    if (!emailPrefs) return;
    const next = { ...emailPrefs, [key]: value };
    setEmailPrefs(next); // optimistic
    setPrefsSaving(true);
    setPrefsError("");
    posthog.capture("email_pref_changed", { preference: key, enabled: value });
    try {
      const res = await requests.put("/api/email/prefs", next);
      setEmailPrefs(res.data);
    } catch {
      setEmailPrefs(emailPrefs); // revert
      setPrefsError("Couldn't save email preferences. Please try again.");
    } finally {
      setPrefsSaving(false);
    }
  };

  const saveDisplayName = async (next: string | null) => {
    setDisplayNameSaving(true);
    setDisplayNameError("");
    try {
      const res = await requests.put<DisplayNameResponse>("/api/account/display-name", { display_name: next });
      setDisplayName(res.data.display_name);
      setDisplayNameInput(res.data.display_name ?? "");
      posthog.capture("display_name_saved", { cleared: !res.data.display_name });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } } | undefined)?.response?.data?.error;
      setDisplayNameError(msg || "Couldn't save display name. Please try again.");
    } finally {
      setDisplayNameSaving(false);
    }
  };

  // Wait for the initial Cognito session check before deciding — otherwise a direct
  // load/refresh sees loggedIn=undefined and wrongly bounces to /login (-> /dashboard).
  if (!authChecked) {
    return (
      <Container className="py-5 text-center">
        <Spinner /> <span className="ms-2">Loading…</span>
      </Container>
    );
  }

  if (!loggedIn) return <Navigate to={authUrlFor("/login")} replace />;

  const handleManageBilling = async () => {
    posthog.capture("billing_portal_opened");
    setPortalLoading(true);
    setError("");
    try {
      const res = await requests.post("/api/billing/portal");
      window.location.href = res.data.portal_url;
    } catch {
      setError("Unable to open billing portal. Please try again.");
      setPortalLoading(false);
    }
  };

  return (
    <div className="account-page" style={{ minHeight: "70vh", paddingTop: "3rem", paddingBottom: "4rem" }}>
      <Container>
        <Row className="justify-content-center">
          <Col lg="9" md="10">
            <h1 className="page-title" style={{ marginBottom: "0.25rem" }}>My Account</h1>
            <p style={{ color: "var(--text-muted)", marginBottom: "2rem" }}>
              {user?.email || user?.username}
            </p>

            {checkoutSuccess && (
              <Alert color="success" className="mb-4">
                🎉 Welcome to DejaChart Pro! Your subscription is now active.
              </Alert>
            )}

            {error && (
              <Alert color="danger" className="mb-4">{error}</Alert>
            )}

            <Row className="g-3 mb-4">
              {/* Plan card */}
              <Col md="6">
                <div
                  style={{
                    border: "1px solid var(--border-color, #e2e8f0)",
                    borderRadius: "12px",
                    padding: "1.75rem",
                    background: "var(--card-background)",
                    boxShadow: "0 2px 8px var(--card-shadow)",
                    height: "100%",
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      Current Plan
                    </h5>
                    {!loading && sub && statusBadge(sub.status)}
                  </div>

                  {loading ? (
                    <div className="text-center py-3">
                      <Spinner size="sm" /> <span className="ms-2">Loading subscription…</span>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: "2rem", fontWeight: 700, margin: "0.5rem 0" }}>
                        {isPro ? "Pro" : "Free"}
                      </p>

                      {sub?.status === "trialing" && sub.trial_end && (
                        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                          Trial ends {formatDate(sub.trial_end)} — you won't be charged until then.
                        </p>
                      )}

                      {sub?.status === "active" && sub.current_period_end && (
                        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem", marginBottom: "0.5rem" }}>
                          {sub.cancel_at_period_end
                            ? `Access ends ${formatDate(sub.current_period_end)}`
                            : `Renews ${formatDate(sub.current_period_end)}`}
                        </p>
                      )}

                      <div className="mt-3 d-flex gap-2 flex-wrap">
                        {isPro ? (
                          <Button
                            color="primary"
                            onClick={handleManageBilling}
                            disabled={portalLoading}
                          >
                            {portalLoading ? <Spinner size="sm" /> : "Manage Billing"}
                          </Button>
                        ) : (
                          <Link to="/pricing">
                            <Button color="primary">Upgrade to Pro</Button>
                          </Link>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Col>

              {/* Quick links */}
              <Col md="6">
                <div
                  style={{
                    border: "1px solid var(--border-color)",
                    borderRadius: "12px",
                    padding: "1.75rem",
                    background: "var(--card-background)",
                    boxShadow: "0 2px 8px var(--card-shadow)",
                    height: "100%",
                  }}
                >
                  <h5 style={{ fontWeight: 600, marginBottom: "1rem" }}>My Tools</h5>
                  <div className="d-flex flex-column gap-2">
                    <Link to="/watchlist" style={{ color: "var(--primary)", textDecoration: "none" }}>
                      📋 My Watchlist
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" style={{ color: "var(--error)", textDecoration: "none" }}>
                        ⚙ Admin Dashboard
                      </Link>
                    )}
                    {isAdmin && (
                      <Link to="/track-record" style={{ color: "var(--error)", textDecoration: "none" }}>
                        📊 Track Record
                      </Link>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Display name */}
            {displayNameLoaded && (
              <div
                style={{
                  border: "1px solid var(--border-color, #e2e8f0)",
                  borderRadius: "12px",
                  padding: "1.75rem",
                  background: "var(--card-background)",
                  boxShadow: "0 2px 8px var(--card-shadow)",
                  marginBottom: "1.5rem",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 style={{ margin: 0, fontWeight: 600 }}>Display Name</h5>
                  {displayNameSaving && <Spinner size="sm" />}
                </div>
                <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
                  Optional — shown instead of your account ID on public leaderboards (like the chart
                  game's, coming soon). Leave blank to stay anonymous there.
                </p>
                {displayNameError && <Alert color="danger" className="py-2">{displayNameError}</Alert>}
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <Input
                    type="text"
                    value={displayNameInput}
                    maxLength={20}
                    placeholder="Anonymous"
                    disabled={displayNameSaving}
                    onChange={(e) => setDisplayNameInput(e.target.value)}
                    style={{ maxWidth: "260px" }}
                  />
                  <Button
                    color="primary"
                    disabled={displayNameSaving || displayNameInput.trim() === (displayName ?? "")}
                    onClick={() => saveDisplayName(displayNameInput.trim() || null)}
                  >
                    Save
                  </Button>
                  {displayName && (
                    <Button
                      color="link"
                      className="text-muted"
                      disabled={displayNameSaving}
                      onClick={() => saveDisplayName(null)}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Email preferences */}
            {emailPrefs && (
              <div
                style={{
                  border: "1px solid var(--border-color, #e2e8f0)",
                  borderRadius: "12px",
                  padding: "1.75rem",
                  background: "var(--card-background)",
                  boxShadow: "0 2px 8px var(--card-shadow)",
                  marginBottom: "1.5rem",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h5 style={{ margin: 0, fontWeight: 600 }}>Email Preferences</h5>
                  {prefsSaving && <Spinner size="sm" />}
                </div>
                {prefsError && <Alert color="danger" className="py-2">{prefsError}</Alert>}
                <FormGroup switch className="mb-2">
                  <Input
                    type="switch"
                    id="pref-digest"
                    checked={emailPrefs.digest_subscribed}
                    disabled={prefsSaving}
                    onChange={(e) => updateEmailPref("digest_subscribed", e.target.checked)}
                  />
                  <Label check for="pref-digest">
                    {isPro ? "Daily digest" : "Weekly recap"}
                    <span style={{ display: "block", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                      {isPro ? "Today's top signals, every trading day" : "The top signals, every Saturday"}
                    </span>
                  </Label>
                </FormGroup>
                <FormGroup switch>
                  <Input
                    type="switch"
                    id="pref-alerts"
                    checked={emailPrefs.alerts_subscribed}
                    disabled={prefsSaving}
                    onChange={(e) => updateEmailPref("alerts_subscribed", e.target.checked)}
                  />
                  <Label check for="pref-alerts">
                    Alert notifications
                    <span style={{ display: "block", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                      Emails when your custom alerts match new signals{isPro ? "" : " (Pro feature)"}
                    </span>
                  </Label>
                </FormGroup>
              </div>
            )}

            {/* Pro features reminder */}
            {!isPro && !loading && (
              <div
                style={{
                  border: "1px solid var(--primary, #3b82f6)",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  background: "var(--card-background)",
                  boxShadow: "0 2px 8px var(--card-shadow)",
                }}
              >
                <h5 style={{ fontWeight: 600, marginBottom: "1rem" }}>Unlock with Pro</h5>
                <ul style={{ paddingLeft: "1.25rem", color: "var(--text-muted)", fontSize: "0.95rem", lineHeight: "2" }}>
                  <li>Unlimited Query Matcher searches (free: 20/day)</li>
                  <li>Personalized feed with custom alerts</li>
                  <li>Alert emails the moment a setup matches</li>
                  <li>Daily email digest</li>
                  <li>Watchlist tracking across every signal</li>
                </ul>
                <Link to="/pricing">
                  <Button color="primary" className="mt-2">See Plans</Button>
                </Link>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}
